export interface ModeratorQueueEntry {
  reportId: string;
  postId: string;
  reason: string;
  category: string;
  details: string | null;
  reporterId: string;
  reporterUsername: string | null;
  assignedModeratorId: string | null;
  assignedModeratorUsername: string | null;
  assignedAt: string | null;
  createdAt: string;
  status: string;
  // Server returns null when the reported post has been deleted — a pending report
  // can outlive its post. Clients must handle this everywhere a group is rendered.
  post: { id: string; username?: string; [k: string]: unknown } | null;
}

export type AssigneeSummary =
  | { kind: 'unassigned' }
  | { kind: 'single'; moderatorId: string; moderatorUsername: string | null }
  | { kind: 'multiple'; count: number }
  | { kind: 'partial'; count: number };

export interface ReportGroup {
  postId: string;
  post: ModeratorQueueEntry['post']; // may be null for deleted posts
  reports: ModeratorQueueEntry[];
  reasons: string[];
  categories: string[];
  reporters: string[];
  assigneeSummary: AssigneeSummary;
  groupCreatedAt: string;
}

function summarizeAssignees(reports: ModeratorQueueEntry[]): AssigneeSummary {
  const assignees = reports.map((r) => r.assignedModeratorId);
  const present = assignees.filter((a): a is string => Boolean(a));
  const allUnassigned = present.length === 0;
  const allAssigned = present.length === reports.length;

  if (allUnassigned) return { kind: 'unassigned' };
  if (!allAssigned) return { kind: 'partial', count: reports.length };

  const unique = new Set(present);
  if (unique.size === 1) {
    const moderatorId = present[0];
    const match = reports.find((r) => r.assignedModeratorId === moderatorId);
    return {
      kind: 'single',
      moderatorId,
      moderatorUsername: match?.assignedModeratorUsername ?? null,
    };
  }
  return { kind: 'multiple', count: unique.size };
}

export function groupReports(entries: ModeratorQueueEntry[]): ReportGroup[] {
  const byPost = new Map<string, ModeratorQueueEntry[]>();
  const order: string[] = [];
  for (const e of entries) {
    if (!byPost.has(e.postId)) {
      byPost.set(e.postId, []);
      order.push(e.postId);
    }
    byPost.get(e.postId)?.push(e);
  }

  return order.map((postId) => {
    const reports = byPost.get(postId)!;
    const reasons = Array.from(new Set(reports.map((r) => r.reason)));
    const categories = Array.from(new Set(reports.map((r) => r.category)));
    const reporterIds = Array.from(new Set(reports.map((r) => r.reporterId)));
    const reporters = reporterIds.map((id) => {
      const match = reports.find((r) => r.reporterId === id);
      return match?.reporterUsername ?? id;
    });
    const groupCreatedAt = reports.map((r) => r.createdAt).sort()[0];

    return {
      postId,
      post: reports[0].post,
      reports,
      reasons,
      categories,
      reporters,
      assigneeSummary: summarizeAssignees(reports),
      groupCreatedAt,
    };
  });
}
